import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Task, TaskComment, TaskCommentFieldAnchor } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { TaskMode, TaskTypeModule } from '../../taskTypes/module';
import { useTaskTypeRuntime } from '../../taskTypes/useTaskTypeRuntime';
import {
  abandonTask,
  aiDetectionComments,
  isExpectedCommandRejection,
  submitTask,
  updateTaskTitle,
} from '../../services/tasks';
import { appendActivity } from '../../services/taskActivity';
import { ValidatorComment } from '../../services/taskValidator';
import { logGlobalError } from '../../utils/globalErrorLogger';
import { taskCommandErrorMetadata } from '../../services/taskCommands';
import { deriveMode } from './deriveMode';
import { isTaskPagePaused } from '../../domain/campaigns/pause';
import {
  DraftSaveContext,
  TaskSessionActor,
} from './taskSessionController';
import { useTaskSessionController } from './useTaskSessionController';
import { useTaskCommentsController } from './useTaskCommentsController';
import { useTaskReviewController } from './useTaskReviewController';
import { useTaskValidationController } from './useTaskValidationController';

const PENDING_ACTION_WAIT_MS = 10000;
const PENDING_ACTION_POLL_MS = 100;

export type TaskDetailAction =
  | 'saveDraft'
  | 'submit'
  | 'abandon'
  | 'approve'
  | 'requestChanges'
  | 'escalate'
  | 'rework'
  | 'returnToQueue'
  | 'claimReview'
  | 'releaseReview'
  | 'addFieldComment'
  | 'addReply'
  | 'editOwnComment'
  | 'deleteOwnComment'
  | 'renameTask'
  | 'markAddressed'
  | 'moderateLlm'
  | 'runValidation';


export interface TaskDetailViewModel {
  busy: boolean;
  campaignPaused?: boolean;
  error: string | null;
  task: Task | null;
  module: TaskTypeModule<any, any> | null;
  runtimeStatus: 'idle' | 'loading' | 'ready' | 'error';
  runtimeError: Error | null;
  runtimeStaleDeployment: boolean;
  retryRuntime: () => void;
  mode: TaskMode;
  canClaimReview: boolean;
  canEditResult: boolean;
  draftResult: unknown;
  setDraftResult: (next: unknown) => void;
  pendingAction: TaskDetailAction | null;
  pendingCommentId: string | null;
  actionError: string | null;
  comments: TaskComment[];
  flushForReload: () => Promise<void>;
  saveDraft: () => Promise<void>;
  submit: () => Promise<boolean>;
  abandon: () => Promise<boolean>;
  approve: () => Promise<boolean>;
  /** Owner-driven reopen of an approved task (status flips to changes_requested
   *  so it lands in the rework queue). Returns true on success. */
  rework: () => Promise<boolean>;
  /** Owner-driven pullback of an awaiting-review submission so it can be edited. */
  returnToQueue: () => Promise<boolean>;
  /** True when the current viewer is the owner AND the task is approved —
   *  used to gate the Rework button in the preview footer. */
  canRework: boolean;
  /** True when the current viewer owns an in_review task that no reviewer
   *  has claimed yet — used to gate the Move back to queue button. */
  canReturnToQueue: boolean;
  requestChangesAction: (comment: string) => Promise<boolean>;
  escalate: (note: string) => Promise<boolean>;
  canEscalateReview: boolean;
  claimForReview: () => Promise<boolean>;
  /** Reviewer-initiated unclaim — drops the reviewer pointer and leaves the
   *  task in the awaiting-review pool. Returns true on success. */
  releaseFromReview: () => Promise<boolean>;
  /** True when the current viewer is the active reviewer on an in_review
   *  task — used to gate the Release-review button in the header. */
  canReleaseReview: boolean;
  addFieldComment: (
    fieldPath: string,
    text: string,
    fieldAnchor?: TaskCommentFieldAnchor,
  ) => Promise<void>;
  addReply: (rootCommentId: string, text: string) => Promise<void>;
  editComment: (commentId: string, text: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  setTitle: (title: string) => Promise<void>;
  canRename: boolean;
  markAddressed: (commentId: string, addressed: boolean) => Promise<void>;
  approveLlmComment: (commentId: string) => Promise<void>;
  editLlmComment: (commentId: string, text: string) => Promise<void>;
  deleteLlmComment: (commentId: string) => Promise<void>;

  // ---- Pre-submit validator (transient, work-mode only) ----
  /** True when the campaign-task-type binding has llmValidator.enabled AND
   *  the task type module declares hasLlmValidator. Drives whether the
   *  Validate button shows in the shell footer. */
  validatorEnabled: boolean;
  /** Validator comments grouped by fieldPath. Empty until runValidation
   *  is called. Replaced wholesale on each subsequent run (overwrite
   *  semantics — re-running clears prior dismissals too). */
  validatorCommentsByField: Map<string, ValidatorComment[]>;
  /** Per-comment dismiss set. Keyed by `${fieldPath}::${originalIndex}`. */
  dismissedValidatorIds: Set<string>;
  /** Bumped when the verification rail should be pulled open (a submit
   *  refused for AI-detected content). Consumers open the rail on change. */
  verificationFocusToken: number;
  /** Last-validation status, drives the button label/badge. */
  validatorStatus: 'idle' | 'running' | 'done' | 'error';
  /** Last-validation error message (when status is 'error'). */
  validatorError: string | null;
  /** Run the validator against the current draft result. Returns true on
   *  success (any comments returned, including empty). The shell calls this
   *  via the onRunValidation action when the user clicks Validate. */
  runValidation: () => Promise<boolean>;
  /** Hide a single validator comment from view. Survives until the next
   *  runValidation call (overwrite semantics) or until submit/dispose. */
  dismissValidatorComment: (fieldPath: string, originalIndex: number) => void;
  /** Restore a locally resolved validator comment to the unresolved list. */
  restoreValidatorComment: (fieldPath: string, originalIndex: number) => void;

  initialize: () => void;
  dispose: () => void;
}

export function useTaskDetailViewModel(taskId: string | undefined): TaskDetailViewModel {
  const { currentUser, isAdmin, reviewCampaignIds } = useAuth();
  // Route navigation reuses this hook while the controllers switch context.
  // Keep the disposer stable; the ref supplies only the latest logging id.
  const taskIdRef = useRef(taskId);
  taskIdRef.current = taskId;
  const [pagePendingAction, setPagePendingAction] = useState<TaskDetailAction | null>(null);
  const [pageActionError, setPageActionError] = useState<string | null>(null);
  const pendingActionRef = useRef<TaskDetailAction | null>(null);
  const uid = currentUser?.uid ?? null;

  const actor = useMemo<TaskSessionActor | null>(() => {
    if (!currentUser) return null;
    return {
      uid: currentUser.uid,
      email: currentUser.email,
      displayName: currentUser.displayName,
    };
  }, [currentUser]);

  const runtimeModuleRef = useRef<TaskTypeModule<any, any> | null>(null);
  const emptyResultForTask = useCallback((nextTask: Task) => {
    const runtimeModule = runtimeModuleRef.current;
    return runtimeModule?.id === nextTask.taskType ? runtimeModule.emptyResult : {};
  }, []);

  const session = useTaskSessionController({
    taskId,
    actor,
    emptyResultForTask,
  });
  const {
    controller: sessionController,
    busy,
    error,
    task,
    campaign,
    draftResult,
    saveError,
  } = session;

  const runtime = useTaskTypeRuntime(task?.taskType ?? null);
  const module = runtime.module;
  runtimeModuleRef.current = module;

  useEffect(() => {
    if (!module || !task || task.result != null) return;
    if (sessionController.getSnapshot().hasUnsavedChanges) return;
    sessionController.applyTaskSnapshot(task);
  }, [module, sessionController, task]);

  const saveDraftForReview = useCallback(
    (context: DraftSaveContext) => sessionController.saveDraft(context),
    [sessionController],
  );
  const applyTaskSnapshot = useCallback(
    (nextTask: Task | null) => sessionController.applyTaskSnapshot(nextTask),
    [sessionController],
  );
  const reviewController = useTaskReviewController({
    taskId,
    reviewRound: task?.reviewRound,
    saveDraft: saveDraftForReview,
    applyTaskSnapshot,
    reportError: (err, action, reviewTaskId) => {
      logGlobalError(err, {
        context: `TaskDetail.${action}`,
        metadata: {
          taskId: reviewTaskId,
          action,
          ...taskCommandErrorMetadata(err),
          clientStateAtFailure: task?.id === reviewTaskId ? {
            status: task.status,
            reviewRound: task.reviewRound ?? 0,
            reviewerId: task.reviewerId ?? null,
            ownerId: task.ownerId ?? null,
          } : null,
        },
      });
    },
  });
  const commentsController = useTaskCommentsController({
    taskId,
    taskTitle: task?.title ?? '',
    reviewRound: task?.reviewRound ?? 0,
    actor,
    reportError: (err, action, commentTaskId) => {
      logGlobalError(err, {
        context: `TaskDetail.${action}`,
        metadata: { taskId: commentTaskId },
      });
    },
  });
  // Bumped when something should pull the validation rail open — today
  // only a submit stopped by AI detection. A counter rather than a boolean
  // so repeat refusals re-open a rail the expert closed.
  const [verificationFocusToken, setVerificationFocusToken] = useState(0);

  // Set once AI detection has stopped a submit and the expert has seen the
  // findings. The next Submit carries it and goes through — detection is
  // advice, and false positives on genuine technical prose are real, so the
  // expert keeps the final call. A ref rather than state: it must be read
  // by the submit closure without re-triggering it, and it is intentionally
  // NOT reset when the draft changes, so one warning is all the expert gets
  // per task session.
  const aiDetectionAcknowledgedRef = useRef(false);

  // Scoped to one task: navigating to a different task must not carry the
  // previous task's override and silently skip its first detection run.
  useEffect(() => {
    aiDetectionAcknowledgedRef.current = false;
  }, [taskId]);

  const validationController = useTaskValidationController({
    taskId,
    status: task?.status,
    reportError: (err, validationTaskId) => {
      logGlobalError(err, {
        context: 'TaskDetail.runValidation',
        metadata: { taskId: validationTaskId },
      });
    },
  });
  const reviewControllerInstance = reviewController.controller;
  const commentsControllerInstance = commentsController.controller;
  const validationControllerInstance = validationController.controller;

  const canReview = useMemo(() => {
    if (!task) return false;
    if (isAdmin) return true;
    return reviewCampaignIds.includes(task.campaignId);
  }, [isAdmin, reviewCampaignIds, task]);

  const { mode, canClaimReview } = useMemo(
    () => deriveMode({ uid, task, canReview }),
    [uid, task, canReview],
  );
  const canEditReviewResult = isAdmin && task?.status === 'in_review';
  const canEditResult = mode === 'work' || canEditReviewResult;
  const controllerPendingAction = reviewController.pendingAction
    ?? commentsController.pendingAction
    ?? (validationController.status === 'running' ? 'runValidation' : null);
  const pendingAction = pagePendingAction ?? controllerPendingAction;
  const pendingCommentId = commentsController.pendingCommentId;
  const actionError = pageActionError ?? reviewController.error ?? commentsController.error;

  useEffect(() => {
    pendingActionRef.current = pendingAction;
  }, [pendingAction]);

  useEffect(() => {
    if (saveError) setPageActionError(saveError);
  }, [saveError]);

  const dispose = useCallback(() => {
    commentsControllerInstance.dispose();
    void reviewControllerInstance.dispose();
    validationControllerInstance.dispose();
    void sessionController.dispose().catch((err) => {
      logGlobalError(err, {
        context: 'TaskDetail.dispose-flush',
        metadata: { taskId: taskIdRef.current ?? null },
      });
    });
  }, [commentsControllerInstance, reviewControllerInstance, sessionController, validationControllerInstance]);

  // The validator toggle is enabled iff (a) the task type module declares
  // hasLlmValidator AND (b) the campaign-task-type binding has
  // llmValidator.enabled. Both must be true; either side off hides the
  // Validate button. The server enforces the same gates in
  // validateTaskSubmission, so this is purely a UI hint.
  const validatorEnabled = useMemo(() => {
    if (!module?.hasLlmValidator) return false;
    if (!campaign || !task) return false;
    const entry = campaign.taskTypes.find((t) => t.taskTypeId === task.taskType);
    return entry?.llmValidator?.enabled === true;
  }, [module, campaign, task]);

  const initialize = useCallback(() => {
    sessionController.initialize();
    commentsControllerInstance.initialize();
  }, [commentsControllerInstance, sessionController]);

  const runPageAction = useCallback(
    async (name: TaskDetailAction, fn: () => Promise<void>): Promise<boolean> => {
      if (pendingAction) return false;
      setPagePendingAction(name);
      setPageActionError(null);
      reviewControllerInstance.clearError();
      commentsControllerInstance.clearError();
      try {
        await fn();
        return true;
      } catch (err: any) {
        setPageActionError(err?.message ?? 'Action failed.');
        // Expected rejections (e.g. a submit refused because AI-generated
        // content was detected) are user-flow outcomes, not bugs — surface
        // the message but keep them out of Error Reporting.
        if (!isExpectedCommandRejection(err)) {
          logGlobalError(err, {
            context: `TaskDetail.${name}`,
            metadata: { taskId: task?.id ?? null },
          });
        }
        return false;
      } finally {
        setPagePendingAction(null);
      }
    },
    [commentsControllerInstance, pendingAction, reviewControllerInstance, task?.id],
  );

  const draftSaveContext = useMemo<DraftSaveContext>(() => ({
    editable: canEditResult,
    kind: canEditReviewResult ? 'reviewer' : 'writer',
    actor,
  }), [actor, canEditResult, canEditReviewResult]);

  const setDraftResult = useCallback(
    (next: unknown) => {
      sessionController.setDraftResult(next, draftSaveContext);
    },
    [draftSaveContext, sessionController],
  );

  const saveDraft = useCallback(async () => {
    await runPageAction('saveDraft', async () => {
      await sessionController.saveDraft(draftSaveContext);
    });
  }, [draftSaveContext, runPageAction, sessionController]);

  const flushForReload = useCallback(async () => {
    const startedAt = Date.now();
    while (pendingActionRef.current && Date.now() - startedAt < PENDING_ACTION_WAIT_MS) {
      await new Promise((resolve) => setTimeout(resolve, PENDING_ACTION_POLL_MS));
    }
    if (pendingActionRef.current) {
      throw new Error('Timed out waiting for the current task action to finish.');
    }

    await sessionController.flushForReload(draftSaveContext);
  }, [draftSaveContext, sessionController]);

  const submit = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !task || !module) return false;
    // Read the controller snapshot rather than the rendered `draftResult`: a
    // submit-confirmation modal writes its answers into the draft and submits
    // in the same interaction, so the rendered copy can be a render behind.
    const latestResult = sessionController.getSnapshot().draftResult;
    const validationError = module.isSubmittable(latestResult as any);
    if (validationError) {
      setPageActionError(validationError);
      return false;
    }
    return runPageAction('submit', async () => {
      sessionController.discardPendingAutosave();
      try {
        await submitTask(
          task.id,
          currentUser,
          latestResult as Record<string, unknown>,
          task.reviewRound,
          { acknowledgedAiDetection: aiDetectionAcknowledgedRef.current },
        );
      } catch (err) {
        // A submit stopped for AI-detected content carries the findings.
        // Load them into the validation rail and open it, so the expert sees
        // which fields and why instead of only a banner. Record the
        // acknowledgement so pressing Submit again goes through.
        const findings = aiDetectionComments(err);
        if (findings.length > 0) {
          validationControllerInstance.showComments(findings);
          setVerificationFocusToken((token) => token + 1);
          aiDetectionAcknowledgedRef.current = true;
        }
        throw err;
      }
    });
  }, [currentUser, task, module, runPageAction, sessionController, validationControllerInstance]);

  const abandon = useCallback(async (): Promise<boolean> => {
    return runPageAction('abandon', async () => {
      if (!currentUser || !task) return;
      await abandonTask(task.id);
      await appendActivity(task.id, task.title, 'reopened', task.status, 'available', currentUser);
    });
  }, [currentUser, task, runPageAction]);

  const reviewCommandContext = useMemo(() => ({
    taskId: task?.id ?? taskId,
    reviewRound: task?.reviewRound ?? 0,
    actor,
    canEditReviewResult,
    draftSaveContext,
  }), [actor, canEditReviewResult, draftSaveContext, task?.id, task?.reviewRound, taskId]);

  const approve = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !task || !module || pendingAction) return false;
    // Modules that ship uploaded artifacts stamp a pointer to the bytes on the
    // task doc here. The reviewer signs off on what is currently visible.
    const approvedSubmission = module.deriveApprovedSubmission?.(draftResult as never);
    return reviewController.approve({ ...reviewCommandContext, approvedSubmission });
  }, [currentUser, draftResult, module, pendingAction, reviewCommandContext, reviewController, task]);

  const rework = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !task || pendingAction) return false;
    return reviewController.rework(reviewCommandContext);
  }, [currentUser, pendingAction, reviewCommandContext, reviewController, task]);

  const returnToQueue = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !task || pendingAction) return false;
    return reviewController.returnToQueue(reviewCommandContext);
  }, [currentUser, pendingAction, reviewCommandContext, reviewController, task]);

  const requestChangesAction = useCallback(
    async (comment: string): Promise<boolean> => {
      if (!currentUser || !task || pendingAction) return false;
      return reviewController.requestChanges(reviewCommandContext, comment);
    },
    [currentUser, pendingAction, reviewCommandContext, reviewController, task],
  );

  const escalate = useCallback(
    async (note: string): Promise<boolean> => {
      if (!currentUser || !task || pendingAction) return false;
      return reviewController.escalate(reviewCommandContext, note);
    },
    [currentUser, pendingAction, reviewCommandContext, reviewController, task],
  );

  const claimForReview = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !task || pendingAction) return false;
    return reviewController.claimForReview(reviewCommandContext);
  }, [currentUser, pendingAction, reviewCommandContext, reviewController, task]);

  const releaseFromReview = useCallback(async (): Promise<boolean> => {
    if (!currentUser || !task || pendingAction) return false;
    return reviewController.releaseFromReview(reviewCommandContext);
  }, [currentUser, pendingAction, reviewCommandContext, reviewController, task]);

  const addFieldComment = useCallback(
    async (fieldPath: string, text: string, fieldAnchor?: TaskCommentFieldAnchor) => {
      if (!currentUser || !task || pendingAction) return;
      await commentsController.addFieldComment(fieldPath, text, fieldAnchor);
    },
    [commentsController, currentUser, pendingAction, task],
  );

  const addReply = useCallback(
    async (rootCommentId: string, text: string) => {
      if (!currentUser || !task || pendingAction) return;
      await commentsController.addReply(rootCommentId, text);
    },
    [commentsController, currentUser, pendingAction, task],
  );

  const editComment = useCallback(
    async (commentId: string, text: string) => {
      if (!currentUser || pendingAction) return;
      await commentsController.editComment(commentId, text);
    },
    [commentsController, currentUser, pendingAction],
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      if (pendingAction) return;
      await commentsController.deleteComment(commentId);
    },
    [commentsController, pendingAction],
  );

  const setTitle = useCallback(
    async (title: string) => {
      await runPageAction('renameTask', async () => {
        if (!currentUser || !task) return;
        await updateTaskTitle(task.id, currentUser, title);
      });
    },
    [currentUser, task, runPageAction],
  );

  const markAddressed = useCallback(
    async (commentId: string, addressed: boolean) => {
      if (!currentUser || pendingAction) return;
      await commentsController.markAddressed(commentId, addressed);
    },
    [commentsController, currentUser, pendingAction],
  );

  const approveLlm = useCallback(
    async (commentId: string) => {
      if (!currentUser || pendingAction) return;
      await commentsController.approveLlmComment(commentId);
    },
    [commentsController, currentUser, pendingAction],
  );

  const editLlm = useCallback(
    async (commentId: string, text: string) => {
      if (!currentUser || pendingAction) return;
      await commentsController.editLlmComment(commentId, text);
    },
    [commentsController, currentUser, pendingAction],
  );

  const deleteLlm = useCallback(
    async (commentId: string) => {
      if (pendingAction) return;
      await commentsController.deleteLlmComment(commentId);
    },
    [commentsController, pendingAction],
  );

  useEffect(() => {
    return () => dispose();
  }, [dispose]);

  // The writer owns the rename affordance only while they hold the task.
  // Mirrors the Firestore rule (see tasks/{taskId} update branch).
  const canRename = useMemo(() => {
    if (!task || !uid) return false;
    if (task.ownerId !== uid) return false;
    return task.status === 'claimed'
      || task.status === 'in_progress'
      || task.status === 'changes_requested';
  }, [task, uid]);

  // Owner-driven reopen is offered only on terminal-approved tasks the
  // current viewer owns. Mirrors the Firestore rule that allows the
  // approved → changes_requested transition for the owner.
  const canRework = useMemo(() => {
    if (!task || !uid) return false;
    return task.ownerId === uid && task.status === 'approved';
  }, [task, uid]);

  const canReturnToQueue = useMemo(() => {
    if (!task || !uid) return false;
    return task.ownerId === uid && task.status === 'in_review' && task.reviewerId === null;
  }, [task, uid]);

  // Reviewer-driven unclaim is offered only while the current viewer is the
  // active reviewer on an in_review task. Mirrors the Firestore rule that
  // allows the reviewer to clear their own reviewerId.
  const canReleaseReview = useMemo(() => {
    if (!task || !uid) return false;
    return task.status === 'in_review' && task.reviewerId === uid;
  }, [task, uid]);

  const canEscalateReview = useMemo(() => {
    if (!task || !uid || !campaign?.escalationAdminId) return false;
    return task.status === 'in_review'
      && task.reviewerId === uid
      && campaign.escalationAdminId !== uid;
  }, [campaign?.escalationAdminId, task, uid]);

  // Run the AI pre-submit validator. The controller keeps this session
  // transient and task-scoped; a task switch cancels the old generation.
  const runValidation = useCallback(async (): Promise<boolean> => {
    if (!task || pendingAction) return false;
    return validationController.run();
  }, [pendingAction, task, validationController]);

  // Dismissals remain transient and are replaced by the next successful run.
  const dismissValidatorComment = useCallback(
    (fieldPath: string, originalIndex: number) => {
      validationController.dismiss(fieldPath, originalIndex);
    },
    [validationController],
  );

  const restoreValidatorComment = useCallback(
    (fieldPath: string, originalIndex: number) => {
      validationController.restore(fieldPath, originalIndex);
    },
    [validationController],
  );

  return {
    campaignPaused: isTaskPagePaused(campaign, task, currentUser?.uid, isAdmin),
    busy,
    error,
    task,
    module,
    runtimeStatus: runtime.status,
    runtimeError: runtime.error,
    runtimeStaleDeployment: runtime.staleDeployment,
    retryRuntime: runtime.retry,
    mode,
    canClaimReview,
    canEditResult,
    draftResult,
    setDraftResult,
    pendingAction,
    pendingCommentId,
    actionError,
    comments: commentsController.comments,
    flushForReload,
    saveDraft,
    submit,
    abandon,
    approve,
    rework,
    returnToQueue,
    canRework,
    canReturnToQueue,
    requestChangesAction,
    escalate,
    canEscalateReview,
    claimForReview,
    releaseFromReview,
    canReleaseReview,
    addFieldComment,
    addReply,
    editComment,
    deleteComment,
    setTitle,
    canRename,
    markAddressed,
    approveLlmComment: approveLlm,
    editLlmComment: editLlm,
    deleteLlmComment: deleteLlm,
    validatorEnabled,
    validatorCommentsByField: validationController.commentsByField,
    dismissedValidatorIds: validationController.dismissedIds,
    verificationFocusToken,
    validatorStatus: validationController.status,
    validatorError: validationController.error,
    runValidation,
    dismissValidatorComment,
    restoreValidatorComment,
    initialize,
    dispose,
  };
}
